import type { ComponentPublicInstance, MaybeRefOrGetter } from 'vue'
import { computed, onUnmounted, toValue, watch } from 'vue'

import { track } from '@/utils/tracking'

function filterAttr ([attr, value]) {
  return attr.indexOf('track') === 0 && !!value
}

function removeAttrPrefix ([attr, value]) {
  const name = attr.replace('track', '')
  const key = `${name.charAt(0).toLowerCase()}${name.slice(1)}`
  return [key, value]
}

function getTrackingData (el: HTMLElement): Record<string, unknown> {
  try {
    const attrs = Object.entries(JSON.parse(JSON.stringify(el.dataset)))
    const entries = attrs.filter(filterAttr).map(removeAttrPrefix)
    return entries.length ? Object.fromEntries(entries) : null
  } catch {
    return null
  }
}

export function useTracking (el: MaybeRefOrGetter<HTMLElement | ComponentPublicInstance>, defaults?: Record<string, unknown>) {
  const target = computed<EventTarget>(() => {
    const value = toValue(el)
    if (!value) return
    return '$el' in value ? value.$el : value
  })

  const onClick = () => {
    const data = getTrackingData(target.value as HTMLElement)
    if (data) track({ ...defaults, ...data })
  }

  watch(target, (currentTarget, previousTarget) => {
    if (previousTarget) previousTarget.removeEventListener('click', onClick)
    if (!currentTarget) return
    currentTarget.addEventListener('click', onClick)
  }, { immediate: true })

  onUnmounted(() => {
    if (!target.value) return
    target.value.removeEventListener('click', onClick)
  })
}
